import { baixarArquivo, requisicaoAutenticada } from './http';

/**
 * Prospecção (SISCRED) — os transferentes com crédito de ICMS, mês a mês
 * (`/api/prospeccao`). Solução em teste, só admin.
 *
 * Espelha `apps/api/src/modules/prospeccao/prospeccao.service.ts`. As chaves
 * de consulta ficam sob `['crm', …]`: criar um card aqui muda o pipeline, e
 * mexer no pipeline muda a coluna "CRM" desta lista — uma invalidação cobre as
 * duas telas.
 */

export type Modalidade = 'EXPORTACAO' | 'DIFERIMENTO' | 'INVESTIMENTO';

export const MODALIDADES: readonly { readonly chave: Modalidade; readonly rotulo: string }[] = [
  { chave: 'EXPORTACAO', rotulo: 'Exportação' },
  { chave: 'DIFERIMENTO', rotulo: 'Diferimento' },
  { chave: 'INVESTIMENTO', rotulo: 'Investimento' },
];

export interface SaldoSiscred {
  readonly modalidade: Modalidade;
  readonly tipo: string | null;
  readonly valor: number;
}

export interface ContatoSiscred {
  readonly responsavel: string | null;
  readonly cargo: string | null;
  readonly telefone: string | null;
  readonly email: string | null;
}

export interface AnotacaoDaEmpresa {
  readonly contatos: readonly ContatoSiscred[];
  readonly desagioExportacao: string | null;
  readonly desagioDiferimento: string | null;
  readonly desagioInvestimento: string | null;
  readonly atualizacoes: string | null;
  /** Quando alguém editou no Deca — daí em diante, planilha só completa o vazio. */
  readonly editadaNoDecaEm: string | null;
  /** O mês da planilha que escreveu por último, se veio de planilha. */
  readonly planilhaDe: { readonly ano: number; readonly mes: number } | null;
}

export interface EmpresaProspectada {
  readonly id: string;
  readonly cnpj: string;
  readonly cadIcms: string | null;
  readonly credencial: string | null;
  readonly razaoSocial: string;
  readonly email: string | null;
  readonly telefone: string | null;
  readonly saldos: readonly SaldoSiscred[];
  readonly totalCredito: number;
  /** Não estava na coleta do mês anterior. */
  readonly nova: boolean;
  readonly anotacao: AnotacaoDaEmpresa;
  readonly cadastro: {
    readonly businessId: string;
    readonly ativa: boolean;
    readonly cliente: boolean;
  } | null;
  readonly card: { readonly id: string; readonly colunaNome: string } | null;
}

export interface ColetaResumo {
  readonly id: string;
  readonly ano: number;
  readonly mes: number;
  readonly origem: string;
  readonly arquivo: string | null;
  /** `AAAA-MM-DD`. */
  readonly coletadaEm: string | null;
  readonly empresas: number;
  readonly creditoSomado: number;
  readonly importadaEm: string;
}

export interface ListaDaColeta {
  readonly coleta: ColetaResumo;
  readonly itens: readonly EmpresaProspectada[];
  readonly resumo: {
    readonly empresas: number;
    readonly novas: number;
    readonly noCrm: number;
    readonly creditoSomado: number;
  };
}

export interface ResultadoDaImportacao {
  readonly coleta: ColetaResumo;
  /** Empresas cuja anotação foi criada ou atualizada com o que a planilha trouxe. */
  readonly anotacoesDaPlanilha: number;
  /** Anotações editadas no Deca: só os campos vazios foram completados. */
  readonly anotacoesMantidas: number;
  readonly avisos: readonly string[];
}

/** Uma pessoa da empresa no cadastro do Deca — colaborador ou contato do CRM. */
export interface PessoaDoCadastro {
  readonly nome: string | null;
  readonly cargo: string | null;
  readonly telefone: string | null;
  readonly email: string | null;
}

/** O que o cadastro do Deca sabe da empresa (`GET /prospeccao/empresas/:cnpj/cadastro`). */
export interface CadastroNaProspeccao {
  readonly businessId: string;
  readonly ativa: boolean;
  readonly telefone: string | null;
  readonly email: string;
  readonly perfis: {
    readonly cliente: boolean;
    readonly assessor: boolean;
    readonly fornecedor: boolean;
  };
  /** Com acesso ao Deca. */
  readonly colaboradores: readonly PessoaDoCadastro[];
  /** Sem acesso: os contatos do card no CRM. */
  readonly contatos: readonly PessoaDoCadastro[];
  /** Os créditos do fornecedor, com o deságio de cada tipo (0–100). */
  readonly creditosDoFornecedor: readonly {
    readonly tipoCredito: string;
    readonly credito: number;
    readonly desagio: number;
  }[];
}

/**
 * O "Filtro" da lista — várias opções marcadas juntas. Situação e valor valem
 * juntos; os tipos de crédito marcados somam entre si (Exportação **ou**
 * Investimento).
 */
export interface FiltrosDaColeta {
  readonly busca?: string;
  /** "Créditos acima de", em reais: o total — ou a soma dos tipos marcados. */
  readonly minimo?: number;
  /** Só as que não estavam na coleta do mês anterior. */
  readonly novas?: boolean;
  /** Só as que ainda não têm card no CRM. */
  readonly foraDoCrm?: boolean;
  /** Só quem tem saldo em algum destes tipos de crédito. */
  readonly modalidades?: readonly Modalidade[];
}

export interface AnotacaoParaSalvar {
  readonly contatos: readonly ContatoSiscred[];
  readonly desagioExportacao: string | null;
  readonly desagioDiferimento: string | null;
  readonly desagioInvestimento: string | null;
  readonly atualizacoes: string | null;
}

export const CHAVE_DA_PROSPECCAO = ['crm', 'prospeccao'] as const;

// ─── Robô do SISCRED ─────────────────────────────────────────────────────────

export type StatusDaExecucao = 'EM_ANDAMENTO' | 'CONCLUIDA' | 'FALHOU';
export type EtapaDaExecucao = 'LISTA' | 'LOGIN' | 'EXTRATOS' | 'GRAVACAO';

/** Uma rodada do robô — a automática do dia 10 ou a pedida no "Coletar agora". */
export interface ExecucaoDoRobo {
  readonly id: string;
  readonly ano: number;
  readonly mes: number;
  readonly gatilho: 'AUTOMATICA' | 'MANUAL';
  readonly status: StatusDaExecucao;
  readonly etapa: EtapaDaExecucao;
  /** Na falha, o porquê em código (`LOGIN_RECUSADO`, `SITE_INDISPONIVEL`…). */
  readonly erro: string | null;
  /** Na falha, o porquê em português. */
  readonly mensagem: string | null;
  readonly cadsNaLista: number | null;
  readonly consultados: number;
  /** CADs cujo extrato não abriu em nenhuma tentativa. */
  readonly falhas: readonly string[];
  readonly empresas: number | null;
  readonly coletaId: string | null;
  readonly iniciadaEm: string;
  readonly terminadaEm: string | null;
}

export interface SituacaoDoRobo {
  /** Login e senha do SISCRED configurados no servidor. */
  readonly credenciais: boolean;
  /** Há navegador no servidor. */
  readonly navegador: boolean;
  readonly automatica: boolean;
  /** A automática parou de tentar este mês: o portal recusou o login. */
  readonly automaticaPausada: boolean;
  readonly creditoMinimo: number;
  /** `AAAA-MM-DD` — a coleta automática roda às 14h. */
  readonly proximaColeta: string | null;
  readonly emAndamento: ExecucaoDoRobo | null;
  readonly ultimas: readonly ExecucaoDoRobo[];
}

export const carregarSituacaoDoRobo = (): Promise<SituacaoDoRobo> =>
  requisicaoAutenticada<SituacaoDoRobo>('/prospeccao/robo');

export const coletarAgora = (substituir: boolean): Promise<ExecucaoDoRobo> =>
  requisicaoAutenticada<ExecucaoDoRobo>('/prospeccao/robo/coletas', {
    metodo: 'POST',
    corpo: { substituir },
  });

export const listarColetas = (): Promise<ColetaResumo[]> =>
  requisicaoAutenticada<ColetaResumo[]>('/prospeccao/coletas');

export const importarColeta = (dados: {
  readonly nomeArquivo: string;
  readonly conteudoBase64: string;
  readonly ano: number;
  readonly mes: number;
  readonly substituir: boolean;
}): Promise<ResultadoDaImportacao> =>
  requisicaoAutenticada<ResultadoDaImportacao>('/prospeccao/coletas/importacao', {
    metodo: 'POST',
    corpo: dados,
  });

export const excluirColeta = (id: string): Promise<void> =>
  requisicaoAutenticada<void>(`/prospeccao/coletas/${id}`, { metodo: 'DELETE' });

export function carregarEmpresasDaColeta(
  id: string,
  filtros: FiltrosDaColeta,
): Promise<ListaDaColeta> {
  const query = new URLSearchParams();
  if (filtros.busca !== undefined && filtros.busca.trim() !== '') {
    query.set('busca', filtros.busca.trim());
  }
  if (filtros.minimo !== undefined) query.set('minimo', String(filtros.minimo));
  if (filtros.novas === true) query.set('novas', '1');
  if (filtros.foraDoCrm === true) query.set('foraDoCrm', '1');
  if (filtros.modalidades !== undefined && filtros.modalidades.length > 0) {
    query.set('modalidades', filtros.modalidades.join(','));
  }
  return requisicaoAutenticada<ListaDaColeta>(
    `/prospeccao/coletas/${id}/empresas?${query.toString()}`,
  );
}

export const historicoDaEmpresa = (
  cnpj: string,
): Promise<{ ano: number; mes: number; totalCredito: number }[]> =>
  requisicaoAutenticada(`/prospeccao/empresas/${cnpj}/historico`);

/** Um tipo de crédito do cadastro do fornecedor, com o valor da última coleta do SISCRED. */
export interface CreditoDaColeta {
  readonly tipoCredito: string;
  readonly credito: number;
  /** O deságio anotado na Prospecção, quando é um número. */
  readonly desagio: number | null;
}

export interface CreditosDaUltimaColeta {
  readonly coleta: {
    readonly ano: number;
    readonly mes: number;
    readonly coletadaEm: string | null;
  } | null;
  readonly creditos: readonly CreditoDaColeta[];
  /** Os saldos como o SISCRED mostra, e a soma deles (29/10/2026). */
  readonly saldos: readonly SaldoSiscred[];
  readonly total: number;
}

/** O que a última coleta do SISCRED mostra da empresa, nos tipos de crédito do cadastro. */
export const carregarCreditosDaUltimaColeta = (
  businessId: string,
): Promise<CreditosDaUltimaColeta> =>
  requisicaoAutenticada<CreditosDaUltimaColeta>(`/prospeccao/cadastro/${businessId}/creditos`);

export const carregarCadastroDaEmpresa = (cnpj: string): Promise<CadastroNaProspeccao | null> =>
  requisicaoAutenticada<{ cadastro: CadastroNaProspeccao | null }>(
    `/prospeccao/empresas/${cnpj}/cadastro`,
  ).then((r) => r.cadastro);

export const salvarAnotacao = (
  cnpj: string,
  dados: AnotacaoParaSalvar,
): Promise<AnotacaoDaEmpresa> =>
  requisicaoAutenticada<AnotacaoDaEmpresa>(`/prospeccao/anotacoes/${cnpj}`, {
    metodo: 'PUT',
    corpo: dados,
  });

export const levarAoCrm = (
  empresaId: string,
): Promise<{
  cardId: string;
  businessId: string;
  empresaCadastrada: boolean;
  cardJaExistia: boolean;
  /** Contatos anotados que foram para a empresa, como contatos sem acesso ao Deca. */
  contatosLevados: number;
}> => requisicaoAutenticada(`/prospeccao/empresas/${empresaId}/card`, { metodo: 'POST' });

/** A coleta inteira, no layout da planilha da automação — o nome vem do servidor. */
export const exportarColeta = (coleta: ColetaResumo): Promise<void> =>
  baixarArquivo(
    `/prospeccao/coletas/${coleta.id}/xlsx`,
    `Mailing extrato SISCRED ${String(coleta.mes).padStart(2, '0')}-${coleta.ano}.xlsx`,
  );

const MESES = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
] as const;

/** `(2026, 7)` → `Julho de 2026`. */
export function rotuloDoMes(ano: number, mes: number): string {
  return `${MESES[mes - 1] ?? mes} de ${ano}`;
}

export const OPCOES_DE_MES = MESES.map((nome, i) => ({ valor: String(i + 1), rotulo: nome }));

/**
 * O mês da coleta pelo nome que a automação dá ao arquivo
 * (`Mailing extrato SISCRED 11-07-2026.xlsx` → julho de 2026), para a
 * importação já vir com o mês certo. `null` se o nome não tiver a data.
 */
export function mesDoNomeDoArquivo(nome: string): { ano: number; mes: number } | null {
  const achado = /(\d{2})-(\d{2})-(\d{4})/.exec(nome);
  if (achado === null) return null;
  const mes = Number(achado[2]);
  const ano = Number(achado[3]);
  return mes >= 1 && mes <= 12 ? { ano, mes } : null;
}

/**
 * Soma dos saldos dos tipos de crédito marcados no filtro — o crédito que a
 * lista ordena e destaca quando algum está marcado. Sem nenhum, o total.
 */
export function creditoNoFiltro(
  saldos: readonly SaldoSiscred[],
  modalidades: readonly Modalidade[],
): number {
  return saldos
    .filter((s) => modalidades.length === 0 || modalidades.includes(s.modalidade))
    .reduce((a, s) => a + s.valor, 0);
}

/** Soma dos saldos de uma modalidade. */
export function totalDaModalidade(saldos: readonly SaldoSiscred[], modalidade: Modalidade): number {
  return saldos.filter((s) => s.modalidade === modalidade).reduce((a, s) => a + s.valor, 0);
}

/** "Cadastrar em Empresas" (29/10/2026): entra no cadastro já como fornecedora, sem card. */
export const cadastrarEmpresaDaProspeccao = (
  empresaId: string,
): Promise<{ businessId: string; empresaCadastrada: boolean; jaEraFornecedora: boolean }> =>
  requisicaoAutenticada(`/prospeccao/empresas/${empresaId}/cadastro`, { metodo: 'POST' });

/** "Cadastrar em Pessoas" (29/10/2026): o contato vira colaborador da empresa, sem CPF. */
export const cadastrarPessoaDaProspeccao = (
  empresaId: string,
  contato: { readonly nome: string; readonly telefone: string | null; readonly email: string },
): Promise<{ personId: string; contaJaExistia: boolean }> =>
  requisicaoAutenticada(`/prospeccao/empresas/${empresaId}/pessoas`, {
    metodo: 'POST',
    corpo: contato,
  });
